/**
 * Where the estate's data lives, and how much of each region we can see.
 *
 * Two separate questions are answered here, and conflating them is how a
 * region ends up looking fine when it is actually invisible:
 *   1. Inventory  — which geographies hold workspaces, on which capacities.
 *   2. Coverage   — detail collection (reports, permissions, refreshes) only
 *                   runs for monitored workspaces, so a region with real
 *                   workspaces and nothing monitored contributes no evidence.
 * The second is flagged as a blind spot rather than reported as an empty
 * region.
 */
import React, { useEffect, useState } from "react";
import { fetchRegionCoverage, RegionCoverage } from "../api/client";
import ErrorBanner from "./ErrorBanner";
import { LoadingPanel } from "./Spinner";

export default function RegionCoveragePanel() {
  const [data, setData] = useState<RegionCoverage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchRegionCoverage()
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message ?? "Failed to load region coverage.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <LoadingPanel label="Loading data residency…" />;
  if (error) return <ErrorBanner message={error} />;
  if (!data) return null;

  // Rows the API emits when it could not determine a geography. They are not
  // places, and a sentence built by joining region names reads as nonsense
  // when one of them is "Not yet resolved" — and worse, prescribes the wrong
  // fix, since enabling monitoring does not tell you where the data lives.
  const NON_GEOGRAPHIES = ["Not yet resolved", "Unresolved capacity", "Unknown"];
  const isGeography = (region: string) => !NON_GEOGRAPHIES.includes(region);

  const blindSpots = data.regions.filter((r) => r.hasBlindSpot && isGeography(r.region));
  const unplaced = data.regions.filter((r) => r.workspaceCount > 0 && !isGeography(r.region));
  const multiGeo =
    data.regions.filter((r) => r.workspaceCount > 0 && isGeography(r.region)).length > 1;

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-content">Data residency</h2>
          <p className="text-sm text-content-muted">
            {multiGeo
              ? "Workspace data is spread across more than one geography."
              : "All workspace data is in a single geography."}
            {data.homeRegion && (
              <> Tenant home region is <span className="text-content">{data.homeRegion}</span>.</>
            )}
          </p>
        </div>
        <p className="text-sm text-content-muted">
          <span className="font-medium text-content">
            {data.totalMonitoredWorkspaces} of {data.totalWorkspaces}
          </span>{" "}
          workspaces monitored
        </p>
      </div>

      {blindSpots.length > 0 && (
        <ErrorBanner
          tone="warning"
          message={
            `Nothing is monitored in ${blindSpots
              .map((r) => `${r.region} (${r.workspaceCount} workspaces)`)
              .join(" or ")}. ` +
            // Not "their reports are never collected". The scanner writes the
            // reports table for the whole tenant regardless of monitoring, so
            // that sentence was contradicted by the Reports column a few
            // inches below it — this panel showed 987 reports in a region it
            // had just said had none collected. Overstating a gap costs the
            // same as understating one: the reader stops trusting the parts
            // that are true.
            "Their reports are still inventoried, but permissions, refresh history, " +
            "subscriptions, unused artifacts and refresh-SLA breaches are only collected " +
            "for monitored workspaces — so there is no access or change evidence for " +
            "these. Enable monitoring on the Workspaces page to start collecting it."
          }
        />
      )}

      {data.unresolvedCapacityWorkspaces > 0 && (
        <ErrorBanner
          tone="warning"
          message={
            `${data.unresolvedCapacityWorkspaces} workspace(s) sit on a capacity we cannot read, ` +
            "so their region is unknown. Grant the collector admin rights on those capacities."
          }
        />
      )}

      {/* Separate from the blind-spot banner because the fix is different: a
          workspace with no region is not waiting to be monitored, it is
          waiting to be placed. Enabling monitoring on it would collect
          evidence while still being unable to say which country holds it. */}
      {unplaced.length > 0 && (
        <ErrorBanner
          tone="warning"
          message={
            `${unplaced
              .map((r) => `${r.workspaceCount} workspace(s) ${r.region.toLowerCase()}`)
              .join(", ")}. ` +
            "A workspace with no region has not been seen by the region-aware workspaces " +
            "collector — either it has not run since the workspace appeared, or the record " +
            "is stale and the workspace no longer exists. Run the workspaces collector from " +
            "System Status; if the count does not change, the record is stale."
          }
        />
      )}

      <div className="overflow-x-auto rounded-lg border border-edge">
        <table className="min-w-full text-sm">
          <thead className="bg-surface-hover">
            <tr className="text-left text-content">
              <th className="px-3 py-2 font-medium">Region</th>
              <th className="px-3 py-2 font-medium">Capacities</th>
              <th className="px-3 py-2 font-medium">SKUs</th>
              <th className="px-3 py-2 font-medium text-right">Workspaces</th>
              <th className="px-3 py-2 font-medium text-right">Monitored</th>
              <th className="px-3 py-2 font-medium text-right">Reports</th>
              <th className="px-3 py-2 font-medium text-right">Gateways</th>
              <th className="px-3 py-2 font-medium text-right">Personal</th>
            </tr>
          </thead>
          <tbody>
            {data.regions.map((r) => (
              <tr key={r.region} className="border-t border-edge">
                <td className="px-3 py-2 text-content">
                  <span className="font-medium">{r.region}</span>
                  {r.region === data.homeRegion && (
                    <span className="ml-2 text-xs text-content-muted">home</span>
                  )}
                </td>
                <td className="px-3 py-2 text-content-muted">{r.capacityCount || "—"}</td>
                <td className="px-3 py-2 text-content-muted">
                  {r.skus.length ? r.skus.join(", ") : "—"}
                </td>
                <td className="px-3 py-2 text-right font-mono text-content">{r.workspaceCount}</td>
                <td className="px-3 py-2 text-right font-mono">
                  <span className={r.hasBlindSpot ? "text-warning" : "text-content"}>
                    {r.monitoredWorkspaceCount}
                  </span>
                </td>
                <td className="px-3 py-2 text-right font-mono text-content">{r.reportCount}</td>
                <td className="px-3 py-2 text-right font-mono text-content">{r.gatewayCount}</td>
                <td className="px-3 py-2 text-right font-mono text-content-muted">
                  {r.personalWorkspaceCount}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-content-muted">
        A workspace has no region of its own — it inherits the region of its capacity, or the
        tenant home region when it is on shared capacity. Gateways are counted in every region
        whose data they move.
      </p>
    </section>
  );
}
